import { useEffect } from "react";
import { MdCheck, MdOutlineFace } from "react-icons/md";
import { BsCreditCard } from "react-icons/bs";
import { IoArrowDown, IoArrowForward } from "react-icons/io5";
import { PiMagicWand, PiCoatHanger, PiBrain, PiFlipHorizontal, PiStarFour } from "react-icons/pi";
import VTONCloth from "../assets/pricing/vton-cloth.jpeg";
import VTONModel from "../assets/pricing/vton-model.jpeg";
import VTONResult from "../assets/pricing/vton-result.png";
import RemoveBGBefore from "../assets/pricing/remove-bg-before.jpg";
import RemoveBGAfter from "../assets/pricing/remove-bg-after.png";
import RemoveBGBG from "../assets/pricing/remove-bg-bg.png";

export default function Pricing() {
    useEffect(() => {
        document.title = "Pricing | TryOnStudio";
    }, []);

    const services = [
        {
            title: "Virtual Try-On",
            icon: <PiCoatHanger className="w-8 h-8" />,
            description: "Experience realistic garment virtual try-on with State Of The Art AI",
            pricing: {
                credits: 1,
                unit: "per try-on"
            },
            features: [
                "High-quality Virtual Try-On",
                "Generate Multiple Images",
                "Instant Results",
                "Download & Save Images"
            ],
            gradient: "from-primary-50 to-primary-100",
            iconBg: "bg-white",
            iconColor: "text-primary"
        },
        {
            title: "Studio",
            icon: <PiFlipHorizontal className="w-8 h-8" />,
            description: "Fashion image editing suite with AI-Powered Tools and chat assistance",
            pricing: {
                credits: "0-1",
                unit: "per tool/service"
            },
            features: [
                "Remove Background (1 credit)",
                "Upscale Image (1 credit)",
                "Crop & Resize (1 credit)",
                "Image Captioning (1 credit)",
                "AI Chat Assistant (1 credit/msg)"
            ],
            gradient: "from-secondary-50 to-secondary-100",
            iconBg: "bg-white",
            iconColor: "text-secondary",
            highlight: true
        },
        {
            title: "FLUX.1 Kontext",
            icon: <PiStarFour className="w-8 h-8" />,
            description: "Advanced context-aware generation with premium FLUX models",
            pricing: {
                credits: "5-10",
                unit: "per generation"
            },
            features: [
                "Context-aware AI Models",
                "Premium Quality Output",
                "Fashion Photo Generation",
                "Advanced Customization"
            ],
            gradient: "from-primary-50 to-primary-100",
            iconBg: "bg-white",
            iconColor: "text-primary",
            subServices: [
                { name: "FLUX.1 Kontext Pro", credits: 5 },
                { name: "FLUX.1 Kontext Max", credits: 10 }
            ]
        },
        {
            title: "Reimagine",
            icon: <PiMagicWand className="w-8 h-8" />,
            description: "Transform and modify parts of images while preserving the rest intact",
            pricing: {
                credits: 1,
                unit: "per reimagine"
            },
            features: [
                "Selective Image Editing",
                "Style Preservation",
                "Creative Modifications",
                "Professional Results"
            ],
            gradient: "from-secondary-50 to-secondary-100",
            iconBg: "bg-white",
            iconColor: "text-secondary"
        },
        {
            title: "Generate",
            icon: <PiBrain className="w-8 h-8" />,
            description: "Create stunning fashion images and content with advanced AI models",
            pricing: {
                credits: "3-7",
                unit: "per generation"
            },
            features: [
                "Multiple FLUX Model Options",
                "Outfit Generation",
                "Model Creation",
                "Fashion Photography",
                "Custom Settings"
            ],
            gradient: "from-primary-50 to-primary-100",
            iconBg: "bg-white",
            iconColor: "text-primary",
            subServices: [
                { name: "FLUX.1 dev", credits: 3 },
                { name: "FLUX.1 pro", credits: 6 },
                { name: "FLUX.1.1", credits: 5 },
                { name: "FLUX.1.1 pro ultra", credits: 7 }
            ]
        }
    ];

    const freeFeatures = [
        "Crop & Resize Images",
        "Store Images in Gallery",
        "Download Images",
        "Image Management"
    ];

    return (
        <div className="h-screen overflow-y-scroll bg-gray-50 font-OpenSans">
            {/* Elegant Header */}
            <div className="relative bg-gradient-to-br from-secondary to-secondary-800 text-white py-12 sm:py-16 lg:py-14">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-6 text-center">
                    <h1 className="text-3xl sm:text-4xl lg:text-3xl font-bold font-proxima mb-4 sm:mb-6 lg:mb-4 leading-tight">
                        Pay-As-You-Go Fashion AI Tools
                    </h1>
                    <p className="text-base sm:text-lg lg:text-base text-white/95 mb-6 sm:mb-8 lg:mb-6 max-w-3xl lg:max-w-2xl mx-auto leading-relaxed">
                        Try 5 credits FREE, then pay only for what you use. No subscriptions, no surprises.
                    </p>
                    <div className="inline-flex items-center gap-3 lg:gap-2 bg-white/20 backdrop-blur-sm rounded-full px-6 lg:px-4 py-3 lg:py-2 text-base lg:text-sm font-semibold shadow-lg">
                        <BsCreditCard className="w-5 h-5 lg:w-4 lg:h-4" />
                        <span>5 FREE Credits Included</span>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-6 py-12 sm:py-16 lg:py-10">
                
                {/* Services Table */}
                <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden mb-16 lg:mb-12">
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 sm:px-10 lg:px-6 py-6 sm:py-8 lg:py-5 border-b border-gray-200">
                        <h2 className="text-2xl sm:text-3xl lg:text-2xl font-bold text-gray-900 mb-2 lg:mb-1">Pricing for Fashion AI Tools</h2>
                        <p className="text-base sm:text-lg lg:text-base text-gray-600">Choose from our Fashion AI Tools to streamline your fashion workflow</p>
                    </div>
                    
                    <div className="divide-y divide-gray-100">
                        {services.map((service, index) => (
                            <div key={index} className="px-6 sm:px-10 lg:px-6 py-8 sm:py-10 lg:py-6 hover:bg-blue-50/30 transition-all duration-300 group border-l-4 border-transparent hover:border-secondary/20">
                                {/* Mobile Layout */}
                                <div className="sm:hidden">
                                    <div className="flex items-start gap-5 mb-5">
                                        <div className={`${service.iconBg} ${service.iconColor} p-3 rounded-xl group-hover:scale-105 transition-all duration-300 flex-shrink-0 shadow-sm`}>
                                            {service.icon}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-3 mb-3">
                                                <div className="flex-1">
                                                    <h3 className="text-xl font-bold text-gray-900 mb-1 leading-tight">{service.title}</h3>
                                                    {service.highlight && (
                                                        <span className="bg-gradient-to-r from-secondary to-secondary-800 text-white px-3 py-1.5 rounded-full text-xs font-semibold inline-block">
                                                            ⭐ Most Popular
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-right flex-shrink-0">
                                                    <div className="text-3xl font-bold bg-gradient-to-r from-secondary to-secondary-800 bg-clip-text text-transparent">
                                                        {service.pricing.credits}
                                                    </div>
                                                    <div className="text-sm text-gray-500 font-medium">
                                                        credit{service.pricing.credits === 1 ? '' : 's'}
                                                    </div>
                                                    <div className="text-xs text-gray-400 italic mt-1">
                                                        {service.pricing.unit}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-gray-700 text-base leading-relaxed mb-4">{service.description}</p>
                                    {service.subServices && (
                                        <div className="bg-gray-50 rounded-xl p-4 mb-4">
                                            <div className="text-base font-semibold text-gray-800 mb-3">Model Options:</div>
                                            <div className="space-y-2">
                                                {service.subServices.map((sub, idx) => (
                                                    <div key={idx} className="flex justify-between items-center py-1">
                                                        <span className="text-gray-700 font-medium">{sub.name}</span>
                                                        <span className="font-bold text-gray-900 bg-gray-200 px-2 py-1 rounded-md text-sm">{sub.credits}c</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {service.features && (
                                        <div className="bg-gray-50 rounded-xl p-4 mb-4">
                                            <div className="text-base font-semibold text-gray-800 mb-3">Features & Tools:</div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                {service.features.map((feature, idx) => (
                                                    <div key={idx} className="flex items-center gap-3 py-1">
                                                        <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></div>
                                                        <span className="text-gray-700 font-medium text-sm">{feature}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Desktop Layout */}
                                <div className="hidden sm:flex items-start justify-between gap-8 lg:gap-6">
                                    <div className="flex items-start gap-8 lg:gap-6 flex-1">
                                        <div className={`${service.iconBg} ${service.iconColor} p-4 lg:p-3 rounded-xl group-hover:scale-105 transition-all duration-300 shadow-sm`}>
                                            {service.icon}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-4 lg:gap-3 mb-3 lg:mb-2">
                                                <h3 className="text-2xl lg:text-xl font-bold text-gray-900">{service.title}</h3>
                                                {service.highlight && (
                                                    <span className="bg-gradient-to-r from-secondary to-secondary-800 text-white px-4 lg:px-3 py-1.5 lg:py-1 rounded-full text-sm lg:text-xs font-semibold shadow-sm">
                                                        ⭐ Most Popular
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-gray-700 text-lg lg:text-base mb-4 lg:mb-3 leading-relaxed max-w-2xl lg:max-w-xl">{service.description}</p>
                                            {service.subServices && (
                                                <div className="bg-gray-50 border-2 border-gray-100 rounded-xl p-4 lg:p-3">
                                                    <div className="text-base lg:text-sm font-semibold text-gray-800 mb-3 lg:mb-2">Model Options:</div>
                                                    <div className="grid grid-cols-2 gap-3 lg:gap-2">
                                                        {service.subServices.map((sub, idx) => (
                                                            <div key={idx} className="flex justify-between items-center py-1">
                                                                <span className="text-gray-700 font-medium text-base lg:text-sm">{sub.name}</span>
                                                                <span className="font-bold text-gray-900 bg-gray-200 px-2 lg:px-1.5 py-1 lg:py-0.5 rounded-md text-xm lg:text-xs">{sub.credits}c</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {service.features && (
                                                <div className="bg-gray-50 border-2 border-gray-100 rounded-xl p-4 lg:p-3 mt-4 lg:mt-3">
                                                    <div className="text-base lg:text-sm font-semibold text-gray-800 mb-3 lg:mb-2">Features & Tools:</div>
                                                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 lg:gap-2">
                                                        {service.features.map((feature, idx) => (
                                                            <div key={idx} className="flex items-center gap-3 lg:gap-2 py-1">
                                                                <div className="w-2 h-2 lg:w-1.5 lg:h-1.5 bg-primary rounded-full flex-shrink-0"></div>
                                                                <span className="text-gray-700 font-medium text-base lg:text-sm">{feature}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-4xl lg:text-3xl font-bold bg-gradient-to-r from-secondary to-secondary-800 bg-clip-text text-transparent">
                                            {service.pricing.credits}
                                        </div>
                                        <div className="text-base lg:text-sm text-gray-500 mt-1 font-medium">
                                            credit{service.pricing.credits === 1 ? '' : 's'}
                                        </div>
                                        <div className="text-xs lg:text-xs text-gray-400 italic mt-1">
                                            {service.pricing.unit}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Virtual Try-On Example - Outside layout containers */}
                                {service.title === "Virtual Try-On" && (
                                    <div className="mt-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-100 rounded-xl p-6 mx-6 sm:mx-10 lg:mx-6">
                                        <div className="text-lg font-bold text-gray-800 mb-6 text-center">See Virtual Try-On In Action</div>
                                        
                                        {/* Desktop/Tablet Layout */}
                                        <div className="hidden sm:block">
                                            <div className="grid grid-cols-8 gap-8 items-center">
                                                <div className="text-center col-span-2">
                                                    <div className="bg-white rounded-lg shadow-md border border-blue-200 mb-3">
                                                        <img src={VTONCloth} alt="Garment" className="w-full h-72 rounded-lg object-contain mx-auto" />
                                                    </div>
                                                    <h4 className="font-bold text-gray-800 mb-1">Choose Garment</h4>
                                                    <p className="text-sm text-gray-600">Upload or select a clothing item</p>
                                                </div>

                                                <div className="flex items-center justify-center">
                                                    <div className="text-primary text-2xl">
                                                        <IoArrowForward className="w-6 h-6" />
                                                    </div>
                                                </div>
                                                
                                                <div className="text-center col-span-2">
                                                    <div className="bg-white rounded-lg shadow-md border border-blue-200 mb-3">
                                                        <img src={VTONModel} alt="Model" className="w-full h-72 rounded-lg object-contain mx-auto" />
                                                    </div>
                                                    <h4 className="font-bold text-gray-800 mb-1">Select Model</h4>
                                                    <p className="text-sm text-gray-600">Choose your model or upload your photo</p>
                                                </div>

                                                <div className="flex items-center justify-center">
                                                    <div className="text-primary text-2xl">
                                                        <IoArrowForward className="w-6 h-6" />
                                                    </div>
                                                </div>
                                                
                                                <div className="text-center col-span-2">
                                                    <div className="bg-white rounded-lg shadow-md border border-green-200 mb-3 relative">
                                                        <img src={VTONResult} alt="Virtual Try-On Result" className="w-full h-72 rounded-lg object-contain mx-auto" />
                                                        <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full p-1">
                                                            <MdCheck className="w-4 h-4" />
                                                        </div>
                                                    </div>
                                                    <h4 className="font-bold text-green-700 mb-1">Perfect Try-On!</h4>
                                                    <p className="text-sm text-gray-600">Get realistic try-on results instantly</p>
                                                </div>
                                            </div>
                                            
                                            <div className="mt-6 text-center">
                                                <div className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-full text-sm font-semibold">
                                                    <MdCheck className="w-4 h-4" />
                                                    Only 1 Credit Per Try-On
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Mobile Layout */}
                                        <div className="block sm:hidden">
                                            <div className="space-y-4">
                                                <div className="text-center">
                                                    <div className="bg-white rounded-lg shadow-md border border-blue-200 mb-3">
                                                        <img src={VTONCloth} alt="Garment" className="w-full rounded-lg object-contain mx-auto" />
                                                    </div>
                                                    <h4 className="font-bold text-gray-800 mb-1">Choose Garment</h4>
                                                    <p className="text-sm text-gray-600">Upload a clothing item</p>
                                                </div>
                                                
                                                <div className="flex items-center justify-center">
                                                    <div className="text-primary text-2xl">
                                                        <IoArrowDown className="w-6 h-6" />
                                                    </div>
                                                </div>
                                                
                                                <div className="text-center">
                                                    <div className="bg-white rounded-lg shadow-md border border-blue-200 mb-3">
                                                        <img src={VTONModel} alt="Model" className="w-full rounded-lg object-contain mx-auto" />
                                                    </div>
                                                    <h4 className="font-bold text-gray-800 mb-1">Select Model</h4>
                                                    <p className="text-sm text-gray-600">Choose model or upload photo</p>
                                                </div>
                                                
                                                <div className="flex items-center justify-center">
                                                    <div className="text-primary text-2xl">
                                                        <IoArrowDown className="w-6 h-6" />
                                                    </div>
                                                </div>
                                                
                                                <div className="text-center">
                                                    <div className="bg-white rounded-lg shadow-md border border-green-200 mb-3 relative">
                                                        <img src={VTONResult} alt="Virtual Try-On Result" className="w-full rounded-lg object-contain mx-auto" />
                                                        <div className="absolute -top-1 -right-1 bg-green-500 text-white rounded-full p-1">
                                                            <MdCheck className="w-3 h-3" />
                                                        </div>
                                                    </div>
                                                    <h4 className="font-bold text-green-700 mb-1">Perfect Result!</h4>
                                                    <p className="text-sm text-gray-600">Realistic try-on instantly</p>
                                                </div>
                                                
                                                <div className="text-center mt-4">
                                                    <div className="inline-flex items-center gap-2 bg-primary text-white px-3 py-2 rounded-full text-sm font-semibold">
                                                        <MdCheck className="w-4 h-4" />
                                                        Only 1 Credit
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {service.title === "Studio" && (
                                    <div className="mt-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-100 rounded-xl p-6 mx-6 sm:mx-10 lg:mx-6">
                                        <div className="text-lg font-bold text-gray-800 mb-6 text-center">See Studio In Action</div>
                                        
                                        {/* Desktop/Tablet Layout */}
                                        <div className="hidden sm:block">
                                            <div className="grid grid-cols-8 gap-8 items-center">
                                                <div className="text-center col-span-2">
                                                    <div className="bg-white rounded-lg shadow-md border border-blue-200 mb-3">
                                                        <img src={RemoveBGBefore} alt="Original Image" className="w-full h-72 rounded-lg object-contain mx-auto" />
                                                    </div>
                                                    <h4 className="font-bold text-gray-800 mb-1">Original</h4>
                                                    <p className="text-sm text-gray-600">Upload any image</p>
                                                </div>

                                               <div className="flex items-center justify-center">
                                                    <div className="text-primary text-2xl">
                                                        <IoArrowForward className="w-6 h-6" />
                                                    </div>
                                                </div>
                                                
                                                <div className="text-center col-span-2">
                                                    <div className="bg-white rounded-lg shadow-md border border-blue-200 mb-3 relative">
                                                        <img src={RemoveBGAfter} alt="Background Removed" className="w-full h-72 rounded-lg object-contain mx-auto" />
                                                        <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full p-1">
                                                            <MdCheck className="w-4 h-4" />
                                                        </div>
                                                    </div>
                                                    <h4 className="font-bold text-gray-800 mb-1">Background Removed</h4>
                                                    <p className="text-sm text-gray-600">Clean extraction</p>
                                                </div>

                                                <div className="flex items-center justify-center">
                                                    <div className="text-primary text-2xl">
                                                        <IoArrowForward className="w-6 h-6" />
                                                    </div>
                                                </div>
                                                
                                                <div className="text-center col-span-2">
                                                    <div className="bg-white rounded-lg shadow-md border border-green-200 mb-3 relative">
                                                        <img src={RemoveBGBG} alt="Final Result" className="w-full h-72 rounded-lg object-contain mx-auto" />
                                                        <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full p-1">
                                                            <MdCheck className="w-4 h-4" />
                                                        </div>
                                                    </div>
                                                    <h4 className="font-bold text-green-700 mb-1">Final Result</h4>
                                                    <p className="text-sm text-gray-600">Ready to use</p>
                                                </div>
                                            </div>
                                            
                                            <div className="mt-6 text-center">
                                                <div className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-full text-sm font-semibold">
                                                    <MdCheck className="w-4 h-4" />
                                                    Only 1 Credit Per Tool Usage
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Mobile Layout */}
                                        <div className="block sm:hidden">
                                            <div className="space-y-4">
                                                <div className="text-center">
                                                    <div className="bg-white rounded-lg shadow-md border border-blue-200 mb-3">
                                                        <img src={RemoveBGBefore} alt="Original Image" className="w-full rounded-lg object-contain mx-auto" />
                                                    </div>
                                                    <h4 className="font-bold text-gray-800 mb-1">Original Image</h4>
                                                    <p className="text-sm text-gray-600">Upload any image</p>
                                                </div>
                                                
                                                <div className="flex items-center justify-center">
                                                    <div className="text-primary text-2xl">
                                                        <IoArrowDown className="w-6 h-6" />
                                                    </div>
                                                </div>
                                                
                                                <div className="text-center">
                                                    <div className="bg-white rounded-lg shadow-md border border-blue-200 mb-3 relative">
                                                        <img src={RemoveBGAfter} alt="Background Removed" className="w-full rounded-lg object-contain mx-auto" />
                                                        <div className="absolute -top-1 -right-1 bg-green-500 text-white rounded-full p-1">
                                                            <MdCheck className="w-3 h-3" />
                                                        </div>
                                                    </div>
                                                    <h4 className="font-bold text-gray-800 mb-1">Background Removed</h4>
                                                    <p className="text-sm text-gray-600">Clean extraction</p>
                                                </div>

                                                <div className="flex items-center justify-center">
                                                    <div className="text-primary text-2xl">
                                                        <IoArrowDown className="w-6 h-6" />
                                                    </div>
                                                </div>
                                                
                                                <div className="text-center">
                                                    <div className="bg-white rounded-lg shadow-md border border-green-200 mb-3 relative">
                                                        <img src={RemoveBGBG} alt="Final Result" className="w-full rounded-lg object-contain mx-auto" />
                                                        <div className="absolute -top-1 -right-1 bg-green-500 text-white rounded-full p-1">
                                                            <MdCheck className="w-3 h-3" />
                                                        </div>
                                                    </div>
                                                    <h4 className="font-bold text-green-700 mb-1">Final Result</h4>
                                                    <p className="text-sm text-gray-600">Ready to use</p>
                                                </div>
                                                
                                                <div className="text-center mt-4">
                                                    <div className="inline-flex items-center gap-2 bg-primary text-white px-3 py-2 rounded-full text-sm font-semibold">
                                                        <MdCheck className="w-4 h-4" />
                                                        Only 1 Credit
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Enhanced Benefits Section */}
                <div className="grid sm:grid-cols-2 gap-8 sm:gap-10 lg:gap-6">
                    {/* Free Features */}
                    <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-8 sm:p-10 lg:p-6 border border-primary-200/50 shadow-md">
                        <div className="flex items-center gap-4 lg:gap-3 mb-6 lg:mb-4">
                            <div className="bg-white text-primary p-3.5 lg:p-2.5 rounded-xl shadow-sm">
                                <MdCheck className="w-7 h-7 lg:w-5 lg:h-5" />
                            </div>
                            <div>
                                <h3 className="text-xl sm:text-2xl lg:text-xl font-bold text-gray-900">Always Free</h3>
                                <p className="text-primary text-sm sm:text-base lg:text-sm font-medium">No credits required</p>
                            </div>
                        </div>
                        <ul className="space-y-4 lg:space-y-3">
                            {freeFeatures.map((feature, index) => (
                                <li key={index} className="flex items-center gap-4 lg:gap-3">
                                    <MdCheck className="w-5 h-5 lg:w-4 lg:h-4 text-primary flex-shrink-0" />
                                    <span className="text-base sm:text-lg lg:text-base text-gray-700 font-medium">{feature}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Key Benefits */}
                    <div className="bg-gradient-to-br from-secondary-50 to-secondary-100 rounded-xl p-8 sm:p-10 lg:p-6 border border-secondary-200/50 shadow-md">
                        <div className="flex items-center gap-4 lg:gap-3 mb-6 lg:mb-4">
                            <div className="bg-white text-secondary p-3.5 lg:p-2.5 rounded-xl shadow-sm">
                                <PiStarFour className="w-7 h-7 lg:w-5 lg:h-5" />
                            </div>
                            <div>
                                <h3 className="text-xl sm:text-2xl lg:text-xl font-bold text-gray-900">Why Choose Us</h3>
                                <p className="text-secondary text-sm sm:text-base lg:text-sm font-medium">Premium advantages</p>
                            </div>
                        </div>
                        <ul className="space-y-6 lg:space-y-4">
                            <li className="flex items-start gap-4 lg:gap-3">
                                <div className="bg-white text-secondary p-2.5 lg:p-2 rounded-xl mt-1 flex-shrink-0 shadow-sm">
                                    <BsCreditCard className="w-5 h-5 lg:w-4 lg:h-4" />
                                </div>
                                <div>
                                    <div className="font-bold text-gray-900 mb-2 lg:mb-1 text-base sm:text-lg lg:text-base">Pay As You Go</div>
                                    <div className="text-sm sm:text-base lg:text-sm text-gray-600 leading-relaxed">No monthly fees, subscriptions, or hidden costs</div>
                                </div>
                            </li>
                            <li className="flex items-start gap-4 lg:gap-3">
                                <div className="bg-white text-secondary p-2.5 lg:p-2 rounded-xl mt-1 flex-shrink-0 shadow-sm">
                                    <MdCheck className="w-5 h-5 lg:w-4 lg:h-4" />
                                </div>
                                <div>
                                    <div className="font-bold text-gray-900 mb-2 lg:mb-1 text-base sm:text-lg lg:text-base">Instant Results</div>
                                    <div className="text-sm sm:text-base lg:text-sm text-gray-600 leading-relaxed">Professional quality output in seconds</div>
                                </div>
                            </li>
                            <li className="flex items-start gap-4 lg:gap-3">
                                <div className="bg-white text-secondary p-2.5 lg:p-2 rounded-xl mt-1 flex-shrink-0 shadow-sm">
                                    <PiStarFour className="w-5 h-5 lg:w-4 lg:h-4" />
                                </div>
                                <div>
                                    <div className="font-bold text-gray-900 mb-2 lg:mb-1 text-base sm:text-lg lg:text-base">Premium AI Models</div>
                                    <div className="text-sm sm:text-base lg:text-sm text-gray-600 leading-relaxed">State-of-the-art technology and algorithms</div>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-br from-secondary to-secondary-800 text-white mb-10">
                <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-6 py-20 lg:py-14 text-center">
                    <h2 className="text-4xl sm:text-5xl lg:text-3xl font-bold mb-6 lg:mb-4 leading-tight">
                        Ready to Get Started?
                    </h2>
                    <p className="text-xl sm:text-2xl lg:text-lg text-white/90 mb-10 lg:mb-6 max-w-3xl lg:max-w-2xl mx-auto leading-relaxed">
                        Sign up today and get 5 free credits to explore all our Fashion AI Tools.
                    </p>
                    <button className="bg-white text-secondary-800 hover:bg-gray-100 font-bold text-base lg:text-xm px-10 lg:px-8 py-4 lg:py-3 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300">
                        Start Free Trial
                    </button>
                    <p className="text-sm lg:text-xs text-white/80 mt-6 lg:mt-4">
                        No credit card required • Get started in seconds
                    </p>
                </div>
            </div>
        </div>
    );
}